import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShoppingCart, faBars, faArrowLeft, faArrowRight, faStar } from '@fortawesome/free-solid-svg-icons';
import one from '../assets/images/one.webp';
import two from '../assets/images/two.webp';
import three from '../assets/images/three.webp';

const images = [
  {
    src: one,
    title: "Luxury Sofa",
    description: "This is a luxurious and comfortable sofa perfect for your living room. It blends style and comfort seamlessly.",
    price: "$499",
    height: "90cm",
    width: "150cm",
    rating: "8.5",
    reviews: "120 reviews",
    colors: ["#D0CCC7", "#00FF00", "#0000FF"]
  },
  {
    src: two,
    title: "Modern Armchair",
    description: "A modern armchair that combines simplicity and elegance, ideal for any room setting.",
    price: "$299",
    height: "70cm",
    width: "80cm",
    rating: "8.0",
    reviews: "95 reviews",
    colors: ["#474747", "#800080", "#FFFF00"]
  },
  {
    src: three,
    title: "Classic Recliner",
    description: "Experience maximum comfort with this classic recliner, perfect for relaxation.",
    price: "$399",
    height: "85cm",
    width: "140cm",
    rating: "8.7",
    reviews: "150 reviews",
    colors: ["#C3B6A4", "#5F9EA0", "#D2B48C"]
  }
];

const ProductPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(images[0].colors[0]);
  const [fade, setFade] = useState(false);
  const [activeButton, setActiveButton] = useState("buyNow");
  const [cartCount, setCartCount] = useState(0); // Cart count state

  const incrementQuantity = () => setQuantity(quantity + 1);
  const decrementQuantity = () => quantity > 1 && setQuantity(quantity - 1);

  const handleNextImage = () => {
    setFade(true);
    setTimeout(() => {
      setCurrentIndex((currentIndex + 1) % images.length);
      setSelectedColor(images[(currentIndex + 1) % images.length].colors[0]);
      setFade(false);
    }, 300);
  };

  const handlePreviousImage = () => {
    setFade(true);
    setTimeout(() => {
      setCurrentIndex((currentIndex - 1 + images.length) % images.length);
      setSelectedColor(images[(currentIndex - 1 + images.length) % images.length].colors[0]);
      setFade(false);
    }, 300);
  };

  const { src, title, description, price, height, width, rating, reviews, colors } = images[currentIndex];

  const addToCart = () => {
    setCartCount(cartCount + quantity); // Update cart count
  };

  return (
    <div className="flex flex-col md:flex-row w-full h-screen">
      {/* Left Section with Logo, Image, and Responsive Nav for Small Screens */}
      <div className="md:w-1/2 bg-gray-100 flex flex-col items-center justify-start p-4 md:p-8 relative">
        {/* Logo at the top left */}
        <div className="absolute top-4 left-4">
          <h1 className="text-2xl font-bold">LOGO</h1>
        </div>

        {/* Responsive Navigation and Icons for Small/Medium Screens */}
        <div className="absolute top-4 right-4 flex items-center space-x-4 md:hidden">
          <FontAwesomeIcon icon={faBars} className="text-xl cursor-pointer hover:text-gray-600" />
          <FontAwesomeIcon icon={faSearch} className="text-xl cursor-pointer hover:text-gray-600" />
          <FontAwesomeIcon icon={faShoppingCart} className="text-xl cursor-pointer hover:text-gray-600" />
        </div>

        {/* Navigation Arrows */}
        <button onClick={handlePreviousImage} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl">
          <FontAwesomeIcon icon={faArrowLeft} className="text-gray-600 hover:text-gray-800" />
        </button>
        <button onClick={handleNextImage} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl">
          <FontAwesomeIcon icon={faArrowRight} className="text-gray-600 hover:text-gray-800" />
        </button>

        {/* Main Product Image with Fade Animation */}
        <img
          src={src}
          alt="Product Image"
          className={`w-[95%] h-[95%] max-w-full max-h-full mt-12 object-cover transition-opacity duration-500 ${fade ? 'opacity-0' : 'opacity-100'}`}
        />
      </div>

      {/* Right Section with Centered Navigation and Product Details */}
      <div className="md:w-1/2 flex flex-col p-4 md:p-8 bg-white h-full items-center">
        {/* Centered Navigation for Large Screens */}
        <nav className="hidden md:flex items-center justify-between space-x-6 text-lg font-medium mb-4 w-4/5">
          <a href="#" className="hover:text-gray-600">Home</a>
          <a href="#" className="hover:text-gray-600">Furniture</a>
          <a href="#" className="hover:text-gray-600">Product</a>
          <a href="#" className="hover:text-gray-600">Store</a>

          {/* Cart and Search Icons for Large Screens */}
          <div className="ml-16 flex space-x-4 items-center top-icons">
            <FontAwesomeIcon icon={faSearch} className="text-xl cursor-pointer hover:text-gray-600" />
            <div className="relative">
              <FontAwesomeIcon icon={faShoppingCart} className="text-xl cursor-pointer hover:text-gray-600" />
              {/* Notification Badge */}
              {cartCount > 0 && (
                <span className="absolute bottom-5 right-0 bg-red-500 text-white rounded-full px-2 text-xs">
                  {cartCount}
                </span>
              )}
            </div>
          </div>
        </nav>

        {/* Product Details Wrapper */}
        <div className="flex flex-col space-y-4 w-4/5 mx-auto justify-center items-center h-full ">
          <div className={`transition-opacity duration-500 ${fade ? 'opacity-0' : 'opacity-100'}`}>
            <div className="uppercase text-sm font-light text-gray-500">New Arrival</div>
            <h1 className="text-3xl md:text-4xl font-bold">{title}</h1>

            {/* Ratings */}
            <div className="flex items-center space-x-2 my-2">
              <FontAwesomeIcon icon={faStar} className="text-yellow-500" />
              <span className="text-lg">{rating}</span>
              <span className="text-gray-500">- {reviews}</span>
            </div>

            {/* Color Picker */}
            <div className="flex space-x-2">
              {colors.map((color) => (
                <span
                  key={color}
                  className={`w-6 h-6 rounded-full cursor-pointer border-2 ${selectedColor === color ? 'border-black' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setSelectedColor(color)}
                ></span>
              ))}
            </div>

            {/* Product Description */}
            <p className="text-lg text-gray-700 leading-relaxed my-4">{description}</p>

            {/* Measurements */}
            <div className="flex space-x-4">
              <div className="w-1/2 bg-transparent p-2 rounded-lg border border-[#EFF2FA]">
                <div className="uppercase text-sm font-light text-gray-500 text-center">Height</div>
                <div className="text-xl font-bold text-center">{height}</div>
              </div>
              <div className="w-1/2 bg-transparent p-2 rounded-lg border border-[#EFF2FA]">
                <div className="uppercase text-sm font-light text-gray-500 text-center">Width</div>
                <div className="text-xl font-bold text-center">{width}</div>
              </div>
            </div>

            {/* Price */}
            <div className="text-2xl font-normal mt-4">{price}</div>
            
            {/* Quantity Counter */}
            <div className="flex items-center space-x-4 mt-4 mb-4">
              <button className="bg-gray-200 px-3 py-1 rounded-full text-lg" onClick={decrementQuantity}>-</button>
              <div className="text-xl font-bold">{quantity}</div>
              <button className="bg-gray-200 px-3 py-1 rounded-full text-lg" onClick={incrementQuantity}>+</button>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 w-4/5">
              <button onClick={addToCart} className="bg-blue-500 text-white px-4 py-3 rounded-sm hover:bg-blue-600 transition duration-200 w-1/2 shadow-lg">
                Add to Cart
              </button>
              <button onClick={() => setActiveButton("buyNow")} className={`bg-[#223047] text-white px-4 py-3 shadow-lg rounded-sm hover:bg-green-600 w-1/2 transition duration-200 ${activeButton === "buyNow" ? "bg-green-700" : ""}`}>
                Buy Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
